<template>
  <div class="metrics-dashboard">
    <!-- Header -->
    <div class="metrics-dashboard__header mb-6">
      <h1 class="metrics-dashboard__title">Métricas del Grupo Inmobiliario</h1>
      <p class="metrics-dashboard__subtitle">
        Conversión y rendimiento de leads
      </p>
      <div
        v-if="filters.start_date && filters.end_date"
        class="metrics-dashboard__period"
      >
        <v-icon small color="#666">mdi-calendar-range</v-icon>
        Período: {{ formatDisplayDate(filters.start_date) }} -
        {{ formatDisplayDate(filters.end_date) }}
      </div>
    </div>

    <!-- Filters -->
    <div class="metrics-dashboard__filters mb-6">
      <v-row>
        <v-col cols="12" md="3">
          <date-range-component
            ref="dateRange"
            v-model="dateRange"
            :default="defaultDateRange"
            label="Rango de fechas"
            dense
            @input="handleDateChange"
          />
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.broker_ids"
            :items="brokers"
            :loading="loadingFilters"
            item-text="name"
            item-value="_id"
            label="Brokers"
            outlined
            dense
            multiple
            clearable
            chips
            small-chips
            deletable-chips
            hide-details
            prepend-inner-icon="mdi-account-tie"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" small>
                <span>{{ item.name }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text text-caption ml-1">
                (+{{ filters.broker_ids.length - 2 }} más)
              </span>
            </template>
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleAllBrokers">
                <v-list-item-action>
                  <v-icon
                    :color="filters.broker_ids.length > 0 ? 'primary' : ''"
                  >
                    {{ brokerIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Seleccionar todos</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.traffic_source_ids"
            :items="trafficSources"
            :loading="loadingFilters"
            item-text="name"
            item-value="_id"
            label="Fuentes de tráfico"
            outlined
            dense
            multiple
            clearable
            chips
            small-chips
            deletable-chips
            hide-details
            prepend-inner-icon="mdi-source-branch"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" small>
                <span>{{ item.name }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text text-caption ml-1">
                (+{{ filters.traffic_source_ids.length - 2 }} más)
              </span>
            </template>
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleAllSources">
                <v-list-item-action>
                  <v-icon
                    :color="
                      filters.traffic_source_ids.length > 0 ? 'primary' : ''
                    "
                  >
                    {{ sourceIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Seleccionar todas</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="3">
          <v-autocomplete
            v-model="filters.funnel_stages"
            :items="phaseOptions"
            item-text="label"
            item-value="value"
            label="Etapas del embudo"
            outlined
            dense
            multiple
            clearable
            chips
            small-chips
            deletable-chips
            hide-details
            prepend-inner-icon="mdi-filter-variant"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 4" small>
                <span>{{ item.label }}</span>
              </v-chip>
              <span v-if="index === 4" class="grey--text text-caption ml-1">
                (+{{ filters.funnel_stages.length - 4 }} más)
              </span>
            </template>
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleAllPhases">
                <v-list-item-action>
                  <v-icon
                    :color="filters.funnel_stages.length > 0 ? 'primary' : ''"
                  >
                    {{ phaseIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Seleccionar todas</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="3">
          <div class="conversion-mode-toggles">
            <label class="conversion-mode-toggles__label">
              <v-icon small class="mr-2">mdi-percent</v-icon>
              Calcular Por
            </label>
            <div class="conversion-mode-toggles__switches">
              <v-switch
                :input-value="filters.conversion_mode === 'total'"
                label="Total de leads"
                inset
                dense
                hide-details
                color="primary"
                class="mt-1"
                @change="setConversionMode('total')"
              />
              <v-switch
                :input-value="filters.conversion_mode === 'previous_stage'"
                label="Etapa previa"
                inset
                dense
                hide-details
                color="primary"
                class="mt-1"
                @change="setConversionMode('previous_stage')"
              />
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <div class="time-metrics-toggles">
            <label class="time-metrics-toggles__label">
              <v-icon small class="mr-2">mdi-clock-outline</v-icon>
              Tiempo a mostrar
            </label>
            <div class="time-metrics-toggles__switches">
              <v-switch
                v-model="showAverage"
                label="Promedio"
                inset
                dense
                hide-details
                color="primary"
                class="mt-1"
              />
              <v-switch
                v-model="showMedian"
                label="Mediana"
                inset
                dense
                hide-details
                color="primary"
                class="mt-1"
              />
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            @click="loadData"
            :loading="loading"
            block
            class="mt-1"
          >
            <v-icon left>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="funnel-skeleton">
      <div class="funnel-skeleton__card">
        <div class="funnel-skeleton__header">
          <div class="funnel-skeleton__bar funnel-skeleton__bar--icon"></div>
          <div class="funnel-skeleton__bar funnel-skeleton__bar--title"></div>
        </div>
        <div class="funnel-skeleton__body">
          <div
            v-for="(width, i) in skeletonWidths"
            :key="i"
            class="funnel-skeleton__stage"
          >
            <div
              class="funnel-skeleton__trapezoid"
              :style="{ width: width + 'px' }"
            ></div>
          </div>
          <div class="funnel-skeleton__bottom">
            <div class="funnel-skeleton__result"></div>
            <div class="funnel-skeleton__result"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <v-alert v-else-if="error" type="error" text>
      {{ error }}
    </v-alert>

    <!-- Conversion Funnel Chart -->
    <v-row v-if="!loading && !error" class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-filter-variant</v-icon>
            Estadísticas - Embudo de Conversión
          </v-card-title>
          <v-card-text>
            <div class="conversion-funnel-container">
              <div class="conversion-funnel">
                <!-- Total Leads -->
                <div class="funnel-stage funnel-stage--total">
                  <div></div>
                  <div class="funnel-trapezoid funnel-trapezoid--base">
                    <div class="funnel-trapezoid__label">TOTAL LEADS</div>
                    <div class="funnel-trapezoid__value">
                      {{ metrics.total_leads }}
                    </div>
                    <div class="funnel-trapezoid__percentage">100%</div>
                  </div>
                  <div></div>
                </div>

                <!-- Dynamic funnel stages -->
                <div
                  v-for="(stage, index) in metrics.funnel"
                  :key="stage.phase"
                  class="funnel-stage"
                >
                  <div></div>
                  <div
                    class="funnel-trapezoid"
                    :style="getFunnelTrapezoidStyle(index)"
                  >
                    <div class="funnel-trapezoid__label">
                      {{ getStageLabel(stage) }}
                    </div>
                    <div class="funnel-trapezoid__value">
                      {{ stage.amount }}
                    </div>
                    <div class="funnel-trapezoid__percentage">
                      {{ stage.pct }}%
                    </div>
                  </div>
                  <div
                    v-if="
                      (stage.avg_hours != null || stage.median_hours != null) &&
                        filters.time_metrics !== null
                    "
                    class="funnel-stage__badges"
                  >
                    <div
                      v-if="showAverage && stage.avg_hours != null"
                      class="funnel-badge"
                      :class="{ 'funnel-badge--single': !showBothMetrics }"
                      :style="getBadgeStyle(index)"
                    >
                      <span v-if="showBothMetrics" class="funnel-badge__label"
                        >PROM</span
                      >
                      <span class="funnel-badge__value">{{
                        formatHours(stage.avg_hours)
                      }}</span>
                    </div>
                    <div
                      v-if="showMedian && stage.median_hours != null"
                      class="funnel-badge"
                      :class="{ 'funnel-badge--single': !showBothMetrics }"
                      :style="getBadgeStyle(index)"
                    >
                      <span v-if="showBothMetrics" class="funnel-badge__label"
                        >MED</span
                      >
                      <span class="funnel-badge__value">{{
                        formatHours(stage.median_hours)
                      }}</span>
                    </div>
                  </div>
                </div>

                <!-- Split connector -->
                <div class="funnel-split">
                  <div class="funnel-split__circle"></div>
                  <div class="funnel-split__lines">
                    <div
                      class="funnel-split__line funnel-split__line--left"
                    ></div>
                    <div
                      class="funnel-split__line funnel-split__line--right"
                    ></div>
                  </div>
                </div>

                <!-- Bottom level: Activos + Descartados -->
                <div class="funnel-bottom">
                  <div class="funnel-result funnel-result--active">
                    <div class="funnel-result__box">
                      <div class="funnel-result__label">ACTIVOS</div>
                      <div class="funnel-result__value">
                        {{ metrics.leads_active.amount }}
                      </div>
                      <div class="funnel-result__percentage">
                        {{ metrics.leads_active.pct }}%
                      </div>
                    </div>
                    <div
                      class="funnel-result__icon funnel-result__icon--active"
                    >
                      <v-icon color="white" size="22">mdi-account-clock</v-icon>
                    </div>
                  </div>

                  <div class="funnel-result funnel-result--discarded">
                    <div class="funnel-result__box">
                      <div class="funnel-result__label">DESCARTADOS</div>
                      <div class="funnel-result__value">
                        {{ metrics.leads_discarded.amount }}
                      </div>
                      <div class="funnel-result__percentage">
                        {{ metrics.leads_discarded.pct }}%
                      </div>
                    </div>
                    <div
                      class="funnel-result__icon funnel-result__icon--discarded"
                    >
                      <v-icon color="white" size="22">mdi-close-circle</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DefaultLayout from "@/components/layouts/DefaultLayout.vue";
import DateRangeComponent from "@/components/real-state-dashboard/MetricsDateRange.vue";
import { fetchRealEstateGroupMetrics } from "@/api/real-estate-group-metrics-api";
import { GetLeadsFilters } from "@/api/lead-api";

const FUNNEL_COLORS = [
  {
    bg: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
    badgeBg: "#fffbeb",
    badgeBorder: "#f59e0b",
    badgeLabel: "#b45309"
  },
  {
    bg: "linear-gradient(135deg, #ff922b 0%, #fd7e14 100%)",
    badgeBg: "#fff4e8",
    badgeBorder: "#fd7e14",
    badgeLabel: "#e8590c"
  },
  {
    bg: "linear-gradient(135deg, #51cf66 0%, #40c057 100%)",
    badgeBg: "#ebfbee",
    badgeBorder: "#40c057",
    badgeLabel: "#2f9e44"
  },
  {
    bg: "linear-gradient(135deg, #da77f2 0%, #be4bdb 100%)",
    badgeBg: "#f8f0fc",
    badgeBorder: "#be4bdb",
    badgeLabel: "#862e9c"
  },
  {
    bg: "linear-gradient(135deg, #4dabf7 0%, #228be6 100%)",
    badgeBg: "#e7f5ff",
    badgeBorder: "#228be6",
    badgeLabel: "#1864ab"
  },
  {
    bg: "linear-gradient(135deg, #63e6be 0%, #099268 100%)",
    badgeBg: "#e6fcf5",
    badgeBorder: "#099268",
    badgeLabel: "#087f5b"
  },
  {
    bg: "linear-gradient(135deg, #ff6b6b 0%, #e03131 100%)",
    badgeBg: "#fff5f5",
    badgeBorder: "#e03131",
    badgeLabel: "#c92a2a"
  },
  {
    bg: "linear-gradient(135deg, #748ffc 0%, #4c6ef5 100%)",
    badgeBg: "#edf2ff",
    badgeBorder: "#4c6ef5",
    badgeLabel: "#3b5bdb"
  }
];

export default {
  name: "RealEstateGroupMetricsDashboard",

  components: {
    DateRangeComponent
  },

  data() {
    return {
      loading: false,
      loadingFilters: false,
      error: null,
      defaultDateRange: "Últimos 7 días",
      dateRange: {},
      showAverage: true,
      showMedian: false,
      filters: {
        start_date: null,
        end_date: null,
        broker_ids: [],
        traffic_source_ids: [],
        conversion_mode: "total",
        funnel_stages: [],
        time_metrics: "avg"
      },
      phaseOptions: [
        { label: "Asignado", value: "assigned" },
        { label: "Por Perfilar", value: "to-contact" },
        { label: "Búsqueda", value: "searching" },
        { label: "Seguimiento", value: "tracking" },
        { label: "Recorrido", value: "scheduled-tour" },
        { label: "Seguimiento post-recorrido", value: "finished-tour" },
        { label: "Ofertando", value: "offer" },
        { label: "Apartado", value: "downpayment" },
        { label: "Promesa", value: "contract" },
        { label: "Cierre", value: "closing-trade" }
      ],
      brokers: [],
      trafficSources: [],
      metrics: {
        total_leads: 0,
        funnel: [],
        leads_active: { amount: 0, pct: 0 },
        leads_discarded: { amount: 0, pct: 0 }
      }
    };
  },

  computed: {
    showBothMetrics() {
      return this.showAverage && this.showMedian;
    },
    brokerIcon() {
      if (this.filters.broker_ids.length === this.brokers.length) {
        return "mdi-close-box";
      }
      if (this.filters.broker_ids.length > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    sourceIcon() {
      if (
        this.filters.traffic_source_ids.length === this.trafficSources.length
      ) {
        return "mdi-close-box";
      }
      if (this.filters.traffic_source_ids.length > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    phaseIcon() {
      if (this.filters.funnel_stages.length === this.phaseOptions.length) {
        return "mdi-close-box";
      }
      if (this.filters.funnel_stages.length > 0) {
        return "mdi-minus-box";
      }
      return "mdi-checkbox-blank-outline";
    },
    skeletonWidths() {
      const stageCount =
        this.filters.funnel_stages.length > 0
          ? this.filters.funnel_stages.length + 1
          : 4;
      return Array.from({ length: stageCount }, (_, i) => {
        const maxW = 440;
        const minW = 220;
        return Math.round(
          maxW - (i * (maxW - minW)) / Math.max(stageCount - 1, 1)
        );
      });
    }
  },

  watch: {
    showAverage() {
      this.updateTimeMetrics();
    },
    showMedian() {
      this.updateTimeMetrics();
    }
  },

  created() {
    this.$emit("update:layout", DefaultLayout);
    this.loadFilters();
  },

  methods: {
    setConversionMode(mode) {
      this.filters.conversion_mode = mode;
    },

    updateTimeMetrics() {
      if (this.showAverage && this.showMedian) {
        this.filters.time_metrics = "both";
      } else if (this.showAverage) {
        this.filters.time_metrics = "avg";
      } else if (this.showMedian) {
        this.filters.time_metrics = "median";
      } else {
        this.filters.time_metrics = null;
      }
    },

    handleDateChange(dateRange) {
      this.filters.start_date = dateRange.start || null;
      this.filters.end_date = dateRange.end || null;
      this.loadData();
    },

    formatHours(avgHours) {
      if (avgHours === null || avgHours === undefined) return "N/A";
      if (avgHours < 1) {
        const mins = Math.round(avgHours * 60);
        return `${mins} min`;
      }
      const days = Math.floor(avgHours / 24);
      const hours = Math.floor(avgHours % 24);
      if (days === 0) return `${hours} hr${hours !== 1 ? "s" : ""}`;
      if (hours === 0) return `${days} día${days !== 1 ? "s" : ""}`;
      const ds = days !== 1 ? "s" : "";
      const hs = hours !== 1 ? "s" : "";
      return `${days} día${ds} ${hours} hr${hs}`;
    },

    formatDisplayDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("es-MX", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    },

    getFunnelTrapezoidStyle(index) {
      const n = this.metrics.funnel.length;
      const maxW = 400;
      const minW = 200;
      const width =
        n <= 1 ? maxW : Math.round(maxW - (index * (maxW - minW)) / (n - 1));

      const color = FUNNEL_COLORS[index % FUNNEL_COLORS.length];

      return {
        background: color.bg,
        width: width + "px"
      };
    },

    getStageLabel(stage) {
      const overrides = { finished: "Cliente" };
      return (overrides[stage.phase] || stage.label).toUpperCase();
    },

    getBadgeStyle(index) {
      const color = FUNNEL_COLORS[index % FUNNEL_COLORS.length];
      return {
        background: color.badgeBg,
        borderColor: color.badgeBorder,
        "--badge-label-color": color.badgeLabel
      };
    },

    toggleAllBrokers() {
      this.$nextTick(() => {
        if (this.filters.broker_ids.length === this.brokers.length) {
          this.filters.broker_ids = [];
        } else {
          this.filters.broker_ids = this.brokers.map(b => b._id);
        }
      });
    },

    toggleAllSources() {
      this.$nextTick(() => {
        if (
          this.filters.traffic_source_ids.length === this.trafficSources.length
        ) {
          this.filters.traffic_source_ids = [];
        } else {
          this.filters.traffic_source_ids = this.trafficSources.map(s => s._id);
        }
      });
    },

    toggleAllPhases() {
      this.$nextTick(() => {
        if (this.filters.funnel_stages.length === this.phaseOptions.length) {
          this.filters.funnel_stages = [];
        } else {
          this.filters.funnel_stages = this.phaseOptions.map(p => p.value);
        }
      });
    },

    async loadFilters() {
      this.loadingFilters = true;
      try {
        const res = await GetLeadsFilters();

        if (res.filters?.contacts) {
          this.brokers = res.filters.contacts
            .filter(
              broker =>
                broker.status !== 0 && broker.role.toLowerCase() !== "developer"
            )
            .map(broker => ({
              _id: broker._id,
              name: broker.name || `${broker.firstName} ${broker.lastName}`
            }));
        }

        if (res.filters?.sources) {
          this.trafficSources = res.filters.sources.map(source => ({
            _id: source.source_id,
            name:
              typeof source.source === "string"
                ? source.source
                : source.source?.name || source.name || "Sin nombre"
          }));
        }
      } finally {
        this.loadingFilters = false;
      }
    },

    async loadData() {
      this.loading = true;
      this.error = null;
      try {
        const params = {};
        if (this.filters.start_date)
          params.start_date = this.filters.start_date;
        if (this.filters.end_date) params.end_date = this.filters.end_date;
        if (this.filters.broker_ids && this.filters.broker_ids.length > 0) {
          params.broker_ids = this.filters.broker_ids;
        }
        if (
          this.filters.traffic_source_ids &&
          this.filters.traffic_source_ids.length > 0
        ) {
          params.traffic_source_ids = this.filters.traffic_source_ids;
        }
        if (this.filters.conversion_mode) {
          params.conversion_mode = this.filters.conversion_mode;
        }
        if (
          this.filters.funnel_stages &&
          this.filters.funnel_stages.length > 0
        ) {
          params.funnel_stages = this.filters.funnel_stages;
        }
        if (this.filters.time_metrics) {
          params.time_metrics = this.filters.time_metrics;
        }

        const res = await fetchRealEstateGroupMetrics(params);
        this.metrics = res.data.data || this.metrics;
      } catch (err) {
        this.error = "Error al cargar las métricas del grupo inmobiliario.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.metrics-dashboard {
  padding: 24px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

/* Header */
.metrics-dashboard__header {
  margin-bottom: 24px;
}

.metrics-dashboard__title {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.metrics-dashboard__subtitle {
  font-size: 14px;
  color: #666;
  margin: 4px 0 0;
}

.metrics-dashboard__period {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666;
  margin-top: 12px;
  font-weight: 500;
}

/* Filters */
.metrics-dashboard__filters {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.conversion-mode-toggles {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  background: #fff;
}

.conversion-mode-toggles__label {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.conversion-mode-toggles__switches {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.time-metrics-toggles {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  background: #fff;
}

.time-metrics-toggles__label {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.time-metrics-toggles__switches {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Conversion Funnel Container */
.conversion-funnel-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px 0;
  min-height: 450px;
}

/* Funnel */
.conversion-funnel {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0;
  position: relative;
  width: 100%;
}

/* Funnel Stages */
.funnel-stage {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 0;
}

.funnel-stage__badges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: -65px;
  align-self: center;
  z-index: 2;
  width: fit-content;
}

.funnel-badge {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 5px;
  border: 1.5px solid #dee2e6;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}

.funnel-badge--single {
  padding: 6px 14px;
}

.funnel-badge__label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--badge-label-color, #666);
}

.funnel-badge--single .funnel-badge__label {
  font-size: 11px;
}

.funnel-badge__value {
  font-size: 11px;
  font-weight: 700;
  color: #1a1a1a;
}

.funnel-badge--single .funnel-badge__value {
  font-size: 14px;
}

.funnel-trapezoid {
  position: relative;
  padding: 16px 28px;
  text-align: center;
  color: white;
  clip-path: polygon(0% 0%, 100% 0%, 90% 100%, 10% 100%);
  transition: all 0.2s ease;
}

.funnel-trapezoid:hover {
  filter: brightness(1.1);
}

/* Total stage - always blue */
.funnel-stage--total .funnel-trapezoid--base {
  background: linear-gradient(135deg, #5c7cfa 0%, #4c6ef5 100%);
  width: 440px;
  clip-path: polygon(0% 0%, 100% 0%, 92% 100%, 8% 100%);
}

.funnel-trapezoid__label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.95;
  margin-bottom: 6px;
}

.funnel-trapezoid__value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 3px;
}

.funnel-trapezoid__percentage {
  font-size: 14px;
  font-weight: 600;
  opacity: 0.9;
}

/* Funnel Split */
.funnel-split {
  position: relative;
  width: 100%;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 4px 0;
}

.funnel-split__circle {
  width: 12px;
  height: 12px;
  background: #adb5bd;
  border-radius: 50%;
  margin-bottom: 6px;
  z-index: 2;
}

.funnel-split__lines {
  position: relative;
  width: 280px;
  height: 28px;
}

.funnel-split__line {
  position: absolute;
  width: 2px;
  height: 28px;
  background: #adb5bd;
  top: -4px;
}

.funnel-split__line--left {
  left: 70px;
  transform: rotate(-25deg);
  transform-origin: top;
}

.funnel-split__line--right {
  right: 70px;
  transform: rotate(25deg);
  transform-origin: top;
}

/* Bottom Results */
.funnel-bottom {
  display: flex;
  gap: 120px;
  justify-content: center;
  margin-top: 8px;
}

.funnel-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.funnel-result__box {
  background: white;
  border-radius: 10px;
  padding: 14px 22px;
  text-align: center;
  min-width: 150px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.funnel-result__box:hover {
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.funnel-result--active .funnel-result__box {
  border: 2px solid #7b1fa2;
}

.funnel-result--discarded .funnel-result__box {
  border: 2px solid #747474;
}

.funnel-result__label {
  font-size: 9px;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 6px;
}

.funnel-result__value {
  font-size: 30px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1;
  margin-bottom: 3px;
}

.funnel-result__percentage {
  font-size: 13px;
  font-weight: 600;
  color: #666;
}

.funnel-result__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.funnel-result__icon--active {
  background: #7b1fa2;
}

.funnel-result__icon--discarded {
  background: #747474;
}

/* Responsive */
@media (max-width: 1200px) {
  .conversion-funnel-container {
    flex-direction: column;
    align-items: center;
  }
}

/* Funnel Skeleton Loader */
@keyframes shimmer {
  0% {
    background-position: -600px 0;
  }
  100% {
    background-position: 600px 0;
  }
}

.funnel-skeleton {
  padding: 0;
}

.funnel-skeleton__card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px 24px 32px;
}

.funnel-skeleton__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.funnel-skeleton__bar {
  border-radius: 6px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

.funnel-skeleton__bar--icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
}

.funnel-skeleton__bar--title {
  width: 260px;
  height: 22px;
}

.funnel-skeleton__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.funnel-skeleton__stage {
  display: flex;
  justify-content: center;
}

.funnel-skeleton__trapezoid {
  height: 72px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
  clip-path: polygon(0% 0%, 100% 0%, 92% 100%, 8% 100%);
  max-width: 100%;
}

.funnel-skeleton__bottom {
  display: flex;
  gap: 120px;
  margin-top: 32px;
}

.funnel-skeleton__result {
  width: 150px;
  height: 88px;
  border-radius: 10px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

.funnel-skeleton__result:last-child {
  animation-delay: 0.15s;
}
</style>
